// O(n) uploader stats for the gallery. Display strings are UI-only; filtering uses uploadedBy.

export interface UploaderDerivedStats {
  uniqueUploaders: string[]
  uploaderCountByName: Map<string, number>
}

export function getUploaderDerivedStats(
  images: ReadonlyArray<{ uploadedBy: string }>
): UploaderDerivedStats {
  const uploaderCountByName = new Map<string, number>()
  for (const img of images) {
    const name = img.uploadedBy
    uploaderCountByName.set(name, (uploaderCountByName.get(name) ?? 0) + 1)
  }
  const uniqueUploaders = [...uploaderCountByName.keys()].sort((a, b) =>
    a.localeCompare(b)
  )
  return { uniqueUploaders, uploaderCountByName }
}

/** All Guests count: backend total when the list is fully loaded, else loaded count. */
export function getAllGuestsDisplayCount(
  hasNextPage: boolean | undefined,
  statsTotal: number,
  loadedCount: number
): number {
  return hasNextPage === false ? statsTotal : loadedCount
}

const UUID_LIKE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isOpaqueUploaderId(id: string): boolean {
  const t = id.trim()
  if (!t) return true
  if (/^anon:/i.test(t)) return true
  if (UUID_LIKE.test(t)) return true
  if (t.length > 36 && !/\s/.test(t)) return true
  return false
}

function truncateDisplayName(s: string, max = 36): string {
  const t = s.trim()
  if (t.length <= max) return t
  return `${t.slice(0, max - 1)}…`
}

// Anonymous 1, 2, … in sorted uniqueUploaders order; non-opaque ids shown truncated (names).
export function buildUploaderDisplayMap(
  uniqueUploaders: string[]
): Map<string, string> {
  const map = new Map<string, string>()
  let n = 0
  for (const id of uniqueUploaders) {
    if (isOpaqueUploaderId(id)) {
      n += 1
      map.set(id, `Anonymous ${n}`)
    } else {
      map.set(id, truncateDisplayName(id))
    }
  }
  return map
}

type WithUploaderLabel = {
  uploadedBy?: string
  uploadedByLabel?: string | null
}

// Prefer uploadedByLabel from API when non-empty; else buildUploaderDisplayMap (anon → Anonymous n).
export function buildMergedUploaderDisplayMap(
  uniqueUploaders: string[],
  images: ReadonlyArray<WithUploaderLabel>
): Map<string, string> {
  const fromApi = new Map<string, string>()
  for (const img of images) {
    const id = img.uploadedBy
    if (id == null || id === '') continue
    const label = img.uploadedByLabel
    if (label != null && String(label).trim() !== '') {
      fromApi.set(id, String(label).trim())
    }
  }
  const fallback = buildUploaderDisplayMap(uniqueUploaders)
  const out = new Map<string, string>()
  for (const id of uniqueUploaders) {
    out.set(id, fromApi.get(id) ?? fallback.get(id) ?? id)
  }
  return out
}
